import { ETypeOfNotification, InputColumnTableProps, InputTable, InputTextCustom, Modal, setNotification } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ExternalHealthEntityPlanDTO, defaultExternalHealthEntityPlanDTO } from '../../../dto/ExternalHealthEntityPlanDTO'
import {
  createExternalHealthEntityPlan,
  deleteExternalHealthEntityPlan,
  getExternalHealthEntityPlans,
  updateExternalHealthEntityPlan,
} from '../../../services/ExternalHealthEntityPlanController'
import LocationInlineEditActions from '../../Location/components/LocationInlineEditActions'
import HealthEntityActionMenu, { HealthEntityActionMenuItem } from '../components/HealthEntityActionMenu'

interface ExternalHealthEntityPlansDialogProps {
  visible: boolean
  setVisible: (visible: boolean) => void
  healthEntityPlanId: number | null
  healthEntityPlanName?: string
}

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function resolveErrorMessage(error: unknown): string {
  if (isRecord(error)) {
    const response = isRecord(error.response) ? error.response : null
    const data = response ? response.data : null
    if (isRecord(data) && typeof data.message === 'string' && data.message.trim() !== '') {
      return data.message
    }
  }
  return 'No se pudo completar la operación'
}

export default function ExternalHealthEntityPlansDialog({
  visible,
  setVisible,
  healthEntityPlanId,
  healthEntityPlanName,
}: Readonly<ExternalHealthEntityPlansDialogProps>) {
  const dispatch = useDispatch()
  const [items, setItems] = useState<ExternalHealthEntityPlanDTO[]>([])
  const [pendingDelete, setPendingDelete] = useState<{ item: ExternalHealthEntityPlanDTO; index: number } | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [itemForm, setItemForm] = useState<ExternalHealthEntityPlanDTO>({ ...defaultExternalHealthEntityPlanDTO })
  const [itemErrors, setItemErrors] = useState<{ externalId?: string; entityName?: string }>({})
  const [editIndex, setEditIndex] = useState<number | null>(null)

  function clearEditor() {
    setItemForm({ ...defaultExternalHealthEntityPlanDTO })
    setItemErrors({})
    setEditIndex(null)
  }

  useEffect(() => {
    if (!visible) return

    clearEditor()

    if (healthEntityPlanId == null) {
      setItems([])
      return
    }

    setLoading(true)
    getExternalHealthEntityPlans(healthEntityPlanId)
      .then((data) => setItems(data ?? []))
      .catch((error) => {
        dispatch(setNotification({
          title: 'Error',
          desc: resolveErrorMessage(error),
          typeOfNotification: ETypeOfNotification.ERROR,
          icon: 'pi pi-times',
        }))
        setItems([])
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, healthEntityPlanId])

  function handleClose() {
    setVisible(false)
    clearEditor()
  }

  async function addOrUpdateItem() {
    if (healthEntityPlanId == null) return false

    const errors: { externalId?: string; entityName?: string } = {}
    if ((itemForm.externalId ?? '').trim() === '') {
      errors.externalId = 'El id. externo es obligatorio'
    }
    if ((itemForm.entityName ?? '').trim() === '') {
      errors.entityName = 'El nombre del plan externo es obligatorio'
    }

    if (Object.keys(errors).length > 0) {
      setItemErrors(errors)
      return false
    }

    const payload: ExternalHealthEntityPlanDTO = {
      externalId: itemForm.externalId.trim(),
      entityName: itemForm.entityName.trim(),
    }

    setSaving(true)
    try {
      if (editIndex === null) {
        const created = await createExternalHealthEntityPlan(healthEntityPlanId, payload)
        setItems(prev => [...prev, created])
      } else {
        const originalExternalId = items[editIndex].externalId
        const updated = await updateExternalHealthEntityPlan(healthEntityPlanId, originalExternalId, payload)
        setItems(prev => prev.map((existing, index) => (index === editIndex ? updated : existing)))
      }
      clearEditor()
      return true
    } catch (error) {
      dispatch(setNotification({
        title: 'Error',
        desc: resolveErrorMessage(error),
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
      return false
    } finally {
      setSaving(false)
    }
  }

  function handleEdit(item: ExternalHealthEntityPlanDTO, index: number) {
    setItemForm({ ...item })
    setItemErrors({})
    setEditIndex(index)
  }

  async function doDelete(item: ExternalHealthEntityPlanDTO, index: number) {
    if (healthEntityPlanId == null) return

    setSaving(true)
    try {
      await deleteExternalHealthEntityPlan(healthEntityPlanId, item.externalId)
      setItems(prev => prev.filter((_item, i) => i !== index))
      if (editIndex === index) {
        clearEditor()
      } else if (editIndex !== null && index < editIndex) {
        setEditIndex(editIndex - 1)
      }
    } catch (error) {
      dispatch(setNotification({
        title: 'Error',
        desc: resolveErrorMessage(error),
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(item: ExternalHealthEntityPlanDTO, index: number) {
    setPendingDelete({ item, index })
  }

  function cancelDelete() {
    setPendingDelete(null)
  }

  function confirmDelete() {
    if (!pendingDelete) return
    const { item, index } = pendingDelete
    setPendingDelete(null)
    void doDelete(item, index)
  }

  const columns: InputColumnTableProps<ExternalHealthEntityPlanDTO>[] = [
    {
      header: 'Id. Externo',
      className: 'w-5',
      body: (item) => item.externalId ?? '',
      input: (
        <InputTextCustom
          className={`${itemErrors.externalId ? '' : 'border-grey'} w-full`}
          value={itemForm.externalId ?? ''}
          disable={editIndex !== null}
          onChange={(e) => {
            setItemForm(prev => ({ ...prev, externalId: e.target.value }))
            setItemErrors(prev => ({ ...prev, externalId: undefined }))
          }}
          error={!!itemErrors.externalId}
          caption={itemErrors.externalId}
        />
      ),
    },
    {
      header: 'Nombre del Plan (externo)',
      className: 'w-5',
      body: (item) => item.entityName ?? '',
      input: (
        <InputTextCustom
          className={`${itemErrors.entityName ? '' : 'border-grey'} w-full`}
          value={itemForm.entityName ?? ''}
          onChange={(e) => {
            setItemForm(prev => ({ ...prev, entityName: e.target.value }))
            setItemErrors(prev => ({ ...prev, entityName: undefined }))
          }}
          error={!!itemErrors.entityName}
          caption={itemErrors.entityName}
        />
      ),
    },
    {
      header: '',
      className: 'health-entity-inline-action-column',
      input: (
        <LocationInlineEditActions
          visible={editIndex !== null}
          onConfirm={() => { void addOrUpdateItem() }}
          onCancel={clearEditor}
        />
      ),
      body: (item, index) => {
        const menuItems: HealthEntityActionMenuItem[] = [
          {
            label: 'Editar',
            icon: 'pi pi-file-edit',
            action: () => handleEdit(item, index),
          },
          {
            label: 'Eliminar',
            icon: 'pi pi-trash',
            action: () => handleDelete(item, index),
          },
        ]

        return (
          <div className='flex justify-end'>
            <HealthEntityActionMenu
              menuItems={menuItems}
              buttonClassName='p-button-text'
              buttonAriaLabel='Opciones del plan externo'
              contentClassName='w-64'
            />
          </div>
        )
      },
    },
  ]

  return (
    <Modal
      className='external-health-entity-plans-modal'
      visible={visible}
      setVisible={setVisible}
      width='w-large'
      onHideCustom={handleClose}
      header={(
        <h1 className='info-dark text-2xl'>
          {`Planes externos${healthEntityPlanName ? ` — ${healthEntityPlanName}` : ''}`}
        </h1>
      )}
      footerButtonLeftText='Cerrar'
      onClickLeftBtn={handleClose}
    >
      <div className='health-entity-table-tab'>
        {loading ? (
          <p>Cargando planes externos...</p>
        ) : (
          <InputTable
            columnGap={4}
            iconAddBtn='pi pi-plus-circle text-3xl'
            headerDivClassName='font-semibold bg-bluegray-50'
            dividerClassName='my-1 border-divider'
            itemsClassName='health-entity-inputtable-item-card'
            buttonColumnWidth={43}
            items={items}
            columns={columns}
            onAdd={addOrUpdateItem}
            divBtnClassName={editIndex !== null ? 'health-entity-inline-editing-hide-add' : ''}
            disabled={saving}
            divider
            dividerInFirstItem
          />
        )}
      </div>

      <Modal
        className='external-health-entity-plans-delete-confirm'
        visible={pendingDelete !== null}
        setVisible={(v) => { if (!v) cancelDelete() }}
        width='w-medium'
        closable={false}
        header={<p className='info-dark text-2xl'>Eliminar plan externo</p>}
      >
        <p>{`¿Seguro que querés eliminar el plan externo "${pendingDelete?.item.entityName}"?`}</p>
        <div className='flex justify-content-end' style={{ marginTop: '1.5rem', marginBottom: '-1.5rem' }}>
          <Button label='Cancelar' className='buttonMain3' onClick={cancelDelete} />
          <Button label='Eliminar' className='buttonMain' onClick={confirmDelete} />
        </div>
      </Modal>
    </Modal>
  )
}
